import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { storageKey } from '@/lib/storage-key.js';

export type Theme = 'dark' | 'light' | 'system';

const DARK_QUERY = '(prefers-color-scheme: dark)';

function prefersDark(): boolean {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia(DARK_QUERY).matches;
}

/**
 * Paint the resolved theme onto <html data-theme>. globals.css keys its light
 * palette off that attribute, so nothing changes visually until this runs —
 * which is why it also has to run at rehydration, not only on user choice.
 */
export function applyTheme(theme: Theme): void {
  if (typeof document === 'undefined') return;
  const resolved = theme === 'system' ? (prefersDark() ? 'dark' : 'light') : theme;
  document.documentElement.setAttribute('data-theme', resolved);
}

type UiState = {
  theme: Theme;
  sidebarOpen: boolean;
  setTheme: (t: Theme) => void;
  toggleSidebar: () => void;
  sidebarCollapsed: boolean;
  toggleSidebarCollapsed: () => void;
  setSidebarCollapsed: (v: boolean) => void;
  searchOpen: boolean;
  setSearchOpen: (v: boolean) => void;
};

export const useUiStore = create<UiState>()(
  persist(
    (set, get) => ({
      theme: 'dark',
      sidebarOpen: false,
      setTheme: (theme) => {
        set({ theme });
        applyTheme(theme);
      },
      toggleSidebar: () => set({ sidebarOpen: !get().sidebarOpen }),
      sidebarCollapsed: true,
      toggleSidebarCollapsed: () => set({ sidebarCollapsed: !get().sidebarCollapsed }),
      setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),
      searchOpen: false,
      setSearchOpen: (searchOpen) => set({ searchOpen }),
    }),
    {
      name: storageKey('ui'),
      partialize: (s) => ({ theme: s.theme, sidebarCollapsed: s.sidebarCollapsed }),
      // index.html ships data-theme="dark"; without this a stored 'light' rehydrates
      // into state but never reaches the DOM, so the page loads dark and only
      // flips once the player re-picks the option.
      onRehydrateStorage: () => (state) => {
        applyTheme(state?.theme ?? 'dark');
      },
    },
  ),
);

// 'system' has to keep tracking the OS after the initial resolve, otherwise it
// behaves as a one-shot snapshot taken at load.
if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
  const mq = window.matchMedia(DARK_QUERY);
  const onChange = () => {
    if (useUiStore.getState().theme === 'system') applyTheme('system');
  };
  if (typeof mq.addEventListener === 'function') {
    mq.addEventListener('change', onChange);
  } else if (typeof (mq as MediaQueryList & { addListener?: unknown }).addListener === 'function') {
    // Safari < 14
    (mq as MediaQueryList & { addListener: (cb: () => void) => void }).addListener(onChange);
  }
}
